import { snapshot, subscribe } from "valtio";
import { useEffect, useMemo, useRef, useState } from "react";

import { isEqual } from "lodash-es";
import { useMemoizedFn } from "ahooks";

/**
 * 类似 Redux 的 useSelector 钩子，用于 Valtio 状态管理
 * @param store Valtio 代理对象
 * @param selector 选择器函数，从 store 中提取所需的状态
 * @param equalityFn 可选的相等性比较函数，默认使用 lodash 的 isEqual
 * @returns 选择器函数返回的状态
 */
export function useSelector<T, S>(
  store: T,
  selector: (state: T) => S,
  equalityFn: (a: S, b: S) => boolean = isEqual,
): S {
  const selectMemo = useMemoizedFn(selector);
  // 获取初始状态，将 selectMemo 和 store 作为依赖
  const initialState = useMemo(
    () => selectMemo(snapshot(store as any)),
    [store, selectMemo],
  );
  const [selectedState, setSelectedState] = useState<S>(() => initialState);

  // 用于存储前一个状态的引用
  const previousStateRef = useRef<S>(selectedState);

  // 创建一个记忆化的更新函数，包含 selectMemo 作为依赖
  const handleUpdate = useMemoizedFn(() => {
    const nextState = selectMemo(snapshot(store as any));

    // 只有当状态发生变化时才更新
    if (!equalityFn(previousStateRef.current, nextState)) {
      previousStateRef.current = nextState;
      setSelectedState(nextState);
    }
  });

  // 当 selectMemo 变化时，立即更新状态
  useEffect(() => {
    const newState = selectMemo(snapshot(store as any));
    if (!equalityFn(previousStateRef.current, newState)) {
      previousStateRef.current = newState;
      setSelectedState(newState);
    }
  }, [selectMemo, equalityFn, store]);

  // 订阅 store 的变化
  useEffect(() => {
    // 初始化时执行一次更新
    handleUpdate();

    // 订阅 store 变化
    const unsubscribe = subscribe(store as any, handleUpdate);

    // 清理订阅
    return unsubscribe;
  }, [store, handleUpdate]);

  return selectedState;
}
